/**
 * hooks/manual-repayments/useManualRepaymentsList.ts
 *
 * List hook for consumer's manual repayment submissions.
 * Registered in listHooks as "manual_repayments".
 */

import {
  CardOutline,
  TimeOutline,
} from "@raresail/react-ionicons";
import {
  IconBox,
  ListItemWrapper,
  StatusTag,
} from "../../components/elements/Elements";
import { API_MANUAL_REPAYMENTS_MY } from "../../constants/apis";
import { MANUAL_REPAYMENTS, SELECT } from "../../constants/constants";
import { searchFilterField } from "../../constants/options";
import {
  addSelectOption,
  currencyFormat,
  formatDate,
  formatDateTime,
  stringify,
} from "../../utils/helpers";
import { openBottomSheet } from "../../utils/modalUtils";

export const useManualRepaymentsList = () => {
  const filters = [
    searchFilterField,
    {
      field: "status",
      type: SELECT,
      options: ["pending", "processed", "rejected"].map((v) =>
        addSelectOption(v)
      ),
    },
  ];

  const viewData = (data: any) => {
    const {
      loan_id,
      amount,
      charges,
      net_amount,
      channel_display,
      currency,
      status,
      company_account_name,
      proof_of_payment,
      external_reference,
      rejection_reason,
      created_at,
    } = data || {};

    return {
      loan: loan_id,
      amount: currencyFormat(amount, currency),
      channel: channel_display || stringify(data?.channel),
      status: <StatusTag status={status} />,
      ...(company_account_name ? { deposited_to: company_account_name } : {}),
      ...(external_reference ? { reference: external_reference } : {}),
      ...(charges && parseFloat(charges) > 0
        ? { charges: currencyFormat(charges), net: currencyFormat(net_amount) }
        : {}),
      ...(proof_of_payment
        ? {
            proof: (
              <a href={proof_of_payment} target="_blank" rel="noopener noreferrer"
                className="main semiBold font12">
                View POP ↗
              </a>
            ),
          }
        : {}),
      ...(rejection_reason ? { rejection_reason } : {}),
      submitted: formatDateTime(created_at),
    };
  };

  const ListItem = ({ data }: { data: any }) => {
    const { loan_id, amount, currency, status, channel_display, created_at } =
      data || {};

    return (
      <ListItemWrapper
        className="flexNullCenter gap15"
        onClick={() =>
          openBottomSheet({
            component: "details",
            title: "Repayment Details",
            source: MANUAL_REPAYMENTS,
            data,
          })
        }
      >
        <IconBox icon={CardOutline} size={20} />

        <div className="flexGrow">
          <div className="font13 semiBold textDark">
            {currencyFormat(amount, currency)}
          </div>
          <div className="font11 grey">
            Loan {loan_id} · {channel_display || "Manual"}
          </div>
          <div className="font10 grey flexNullCenter gap5 mt3">
            <TimeOutline fontSize={11} />
            {formatDate(created_at)}
          </div>
        </div>

        <div className="textEnd">
          <StatusTag status={status} />
        </div>
      </ListItemWrapper>
    );
  };

  return {
    ListItem,
    title: "Manual Repayments",
    subtitle: "Your repayment proof submissions",
    api: API_MANUAL_REPAYMENTS_MY,
    filters,
    viewData,
  };
};